'use client';

import { useState, useRef, useCallback, useEffect, useLayoutEffect, ChangeEvent } from 'react';
import { IconArrowUp, IconPhoneSpark, IconCommand, IconPhotoPlus, IconPlus, IconWaveSine, IconChevronRight } from '@tabler/icons-react';
import { Loader2, X } from 'lucide-react';
import cx from 'classnames';
import { toast } from 'sonner';
import { toastUserError } from '@/lib/errors/toast-user-error';
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
  PromptInputButton,
} from '@/components/ui/shadcn-io/ai/prompt-input';
import {
  Attachments,
  Attachment,
  AttachmentPreview,
  AttachmentRemove,
} from '@/components/ai-elements/attachments';
import { PreviewAttachment } from './preview-attachment';
import { getFloatingInputClasses, WidgetStyle } from '@/lib/chat-interface/widget-styles';
import { logger } from '@/lib/logger';
import { takeSuggestedQuestions } from '@/lib/agents/suggested-questions';
import {
  DEFAULT_FLOATING_PLACEHOLDER,
  useTypewriterPlaceholder,
} from '@/lib/agents/use-typewriter-placeholder';
import { FloatingSuggestedBubbles } from './floating-suggested-bubbles';

const MAX_CHAT_IMAGE_ATTACHMENTS = 2;

interface FloatingInputCoreProps {
  input: string;
  setInput: (input: string) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading?: boolean;
  widgetStyle?: WidgetStyle;
  accentColor?: string;
  chatVoiceEnabled?: boolean;
  theme?: 'light' | 'dark';
  placeholder?: string;
  onVoiceClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  // For drawer mode - no expansion behavior
  drawerMode?: boolean;
  // Attachments support
  attachments?: Array<{ id: string; url?: string; filename?: string; contentType?: string; fileId?: string; [key: string]: any }>;
  onAttachmentRemove?: (id: string) => void;
  onAttachmentAdd?: (attachment: any) => void;
  chatFileAttachementEnabled?: boolean;
  chatbotId?: string;
  threadId?: string;
  inputClassName?: string;
  compact?: boolean;
  suggestedQuestions?: string[];
  onSuggestionSelect?: (question: string) => void;
  onExpand?: () => void;
  hasActiveConversation?: boolean;
  onContinue?: () => void;
}

export function FloatingInputCore({
  input,
  setInput,
  onSubmit,
  isLoading = false,
  widgetStyle = 'white',
  accentColor,
  chatVoiceEnabled = false,
  theme = 'light',
  placeholder = 'Ask a question...',
  onVoiceClick,
  className,
  style: customStyle,
  disabled = false,
  drawerMode = false,
  attachments = [],
  onAttachmentRemove,
  onAttachmentAdd,
  chatFileAttachementEnabled = false,
  chatbotId,
  threadId,
  inputClassName,
  compact = false,
  suggestedQuestions,
  onSuggestionSelect,
  onExpand,
  hasActiveConversation = false,
  onContinue,
}: FloatingInputCoreProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<Array<string>>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const isDark = theme === 'dark';
  
  // Simple ref callback - no manual style manipulation needed (CSS handles it)
  const setTextareaRef = useCallback((element: HTMLTextAreaElement | null) => {
    textareaRef.current = element;
  }, []);

  // Helper function to create disabled version of accent color
  const getDisabledColor = (color: string | undefined) => {
    if (!color) {
      return isDark ? '#a3a3a3' : '#a3a3a3'; // Use softer gray
    }
    // Convert hex to rgba and apply opacity for disabled state
    // Remove # if present
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Return rgba with 40% opacity for disabled state
    return `rgba(${r}, ${g}, ${b}, 0.4)`;
  };

  // Send button: accent color when enabled, disabled accent color when disabled
  const sendButtonBg = (input.trim() || attachments.length > 0) && !isLoading && !disabled
    ? (accentColor || '#000000')
    : getDisabledColor(accentColor);
  
  // Phone button: always enabled (accent color)
  const phoneButtonBg = accentColor || '#000000';

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const minHeight = compact ? 20 : 24;
    const maxHeight = 200;
    // When empty, snap to a single line. Mobile browsers can mis-measure the
    // textarea while the drawer slides up (field-sizing fallback), which would
    // otherwise balloon the empty pill to its max height.
    if (input.trim().length === 0) {
      el.style.height = `${minHeight}px`;
      return;
    }
    el.style.height = 'auto';
    // Keep the resting height at a single line so the flex row can vertically
    // center the (placeholder) text inside the pill.
    el.style.height = `${Math.min(Math.max(el.scrollHeight, minHeight), maxHeight)}px`;
  }, [compact, input]);

  // Run synchronously before paint so the empty textarea never flashes tall.
  useLayoutEffect(() => {
    adjustHeight();
  }, [input, adjustHeight]);

  // The mobile chat drawer slides up on open; the textarea can be mis-measured
  // mid-animation and balloon to its max height. Re-assert the height after the
  // element mounts and again after the transition settles.
  useEffect(() => {
    adjustHeight();
    const raf = requestAnimationFrame(adjustHeight);
    const t1 = setTimeout(adjustHeight, 150);
    const t2 = setTimeout(adjustHeight, 400);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [adjustHeight]);

  // File upload functionality
  const uploadFile = async (file: File) => {
    const { compressImageForUpload } = await import(
      '@/lib/images/compress-image-for-upload'
    );
    let uploadable = file;
    try {
      uploadable = await compressImageForUpload(file);
    } catch {
      uploadable = file;
    }

    const formData = new FormData();
    formData.append('file', uploadable);
    
    // Add chatbotId and threadId for HIPAA-compliant private storage
    if (chatbotId) {
      formData.append('chatbotId', chatbotId);
    }
    if (threadId) {
      formData.append('threadId', threadId);
    }

    try {
      // Public pages / embeds are unauthenticated. Proxy only allows this
      // route without a session when x-embed=1 (same as /api/chat).
      const headers: Record<string, string> = {};
      if (chatbotId) {
        headers['x-embed'] = '1';
        headers['x-chatbot-id'] = chatbotId;
      }

      const response = await fetch('/api/files/upload', {
        method: 'POST',
        headers,
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const { url, pathname, contentType, name, fileId } = data;

        const attachment = {
          id: fileId || url,
          url,
          name: name || pathname,
          contentType: contentType,
          fileId: fileId, // Required for HIPAA-compliant deletion
        };
        
        return attachment;
      } else {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          toastUserError(
            { ...errorData, status: response.status },
            'Upload failed. Please try again.',
          );
        } catch {
          toastUserError(
            { status: response.status },
            'Upload failed. Please try again.',
          );
        }
        return undefined;
      }
    } catch (error) {
      logger.error('Upload error', error instanceof Error ? { message: error.message } : error, 'floating-input-core');
      toast.error('Failed to upload file, please try again!');
      return undefined;
    }
  };

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      const remaining = Math.max(0, MAX_CHAT_IMAGE_ATTACHMENTS - attachments.length);
      if (remaining === 0) {
        toast.error(`Only ${MAX_CHAT_IMAGE_ATTACHMENTS} images are allowed`);
        if (event.target) {
          event.target.value = '';
        }
        return;
      }

      const imageFiles = files.filter((file) => file.type.startsWith('image/'));
      if (imageFiles.length === 0) {
        toast.error('Only image files are supported');
        if (event.target) {
          event.target.value = '';
        }
        return;
      }

      if (imageFiles.length > remaining) {
        toast.error(`Only ${MAX_CHAT_IMAGE_ATTACHMENTS} images are allowed`);
      }

      const filesToUpload = imageFiles.slice(0, remaining);
      setUploadQueue(filesToUpload.map((file) => file.name));

      try {
        const uploadedAttachments = await Promise.all(
          filesToUpload.map((file) => uploadFile(file)),
        );
        const successful = uploadedAttachments.filter(Boolean);

        if (successful.length > 0 && onAttachmentAdd) {
          const slots = Math.max(0, MAX_CHAT_IMAGE_ATTACHMENTS - attachments.length);
          for (const uploadedAttachment of successful.slice(0, slots)) {
            onAttachmentAdd(uploadedAttachment);
          }
        } else {
          toast.error('Failed to upload image. Please try again.');
        }
      } catch (error) {
        logger.error('Error uploading file', error instanceof Error ? { message: error.message } : error, 'floating-input-core');
        toast.error('Failed to upload image. Please try again.');
      } finally {
        setUploadQueue([]);
        // Reset the input value to allow re-selecting the same file
        if (event.target) {
          event.target.value = '';
        }
      }
    },
    [attachments.length, chatbotId, threadId, onAttachmentAdd],
  );

  const deleteFile = async (attachment: any) => {
    if (!attachment.fileId) {
      console.error('Missing fileId for HIPAA-compliant deletion');
      toast.error('Cannot delete file: Missing file identifier');
      return false;
    }

    try {
      const response = await fetch(
        `/api/files/delete?fileId=${attachment.fileId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        return true;
      } else {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          toastUserError(
            { ...errorData, status: response.status },
            'Failed to delete file.',
          );
        } catch {
          toastUserError({ status: response.status }, 'Failed to delete file.');
        }
        return false;
      }
    } catch (error) {
      logger.error('Delete error', error instanceof Error ? { message: error.message } : error, 'floating-input-core');
      toast.error('Failed to delete file, please try again!');
      return false;
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const getInputClasses = () => {
    const baseClasses = getFloatingInputClasses(widgetStyle, theme);
    // In drawer/chat mode: strip the default solid bg and use a muted pill fill
    if (drawerMode) {
      const cleaned = baseClasses
        .replace(/\bbg-\S+/g, '') // Remove all bg-* classes
        .replace(/\btext-\S+/g, '') // Remove default text color (handled via inline style)
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();
      return cx(cleaned, 'bg-neutral-100 dark:bg-neutral-800/80');
    }
    return baseClasses;
  };

  const getTextareaClasses = () => {
    // Don't add text color classes - we handle it via inline style with !important
    return cx(
      'resize-none border-none bg-transparent outline-none',
      'text-sm'
    );
  };

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if ((!input.trim() && attachments.length === 0) || disabled) return;

    onSubmit(e);
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    // Reset expansion states (only if not in drawer mode)
    if (!drawerMode) {
      setIsClicked(false);
      setIsFocused(false);
    }
  }, [input, attachments.length, onSubmit, disabled, drawerMode]);

  const hasContent = input.trim().length > 0 || attachments.length > 0;
  const isActive = isFocused || isClicked || input.trim().length > 0;
  const questions = takeSuggestedQuestions(suggestedQuestions);
  const showSuggestions = !drawerMode && isClicked && !hasActiveConversation && questions.length > 0;
  const isContinue = !drawerMode && hasActiveConversation;
  const typewriterPlaceholder = useTypewriterPlaceholder(
    questions.length > 0 ? questions : [placeholder || DEFAULT_FLOATING_PLACEHOLDER],
    !drawerMode && !isClicked && !hasActiveConversation
  );
  const effectivePlaceholder = isContinue
    ? 'Continue conversation...'
    : !drawerMode
      ? (isClicked ? (placeholder || DEFAULT_FLOATING_PLACEHOLDER) : typewriterPlaceholder)
      : placeholder;
  const onExpandRef = useRef(onExpand);
  onExpandRef.current = onExpand;
  const wasExpandedRef = useRef(false);

  useEffect(() => {
    if (drawerMode || hasActiveConversation) return;
    if (isClicked && !wasExpandedRef.current) {
      onExpandRef.current?.();
    }
    wasExpandedRef.current = isClicked;
  }, [drawerMode, isClicked, hasActiveConversation]);

  useEffect(() => {
    if (drawerMode || !isClicked) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsClicked(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [drawerMode, isClicked]);
  // Thin focus ring: accent color when set, otherwise black (light) / white (dark)
  const ringColor = accentColor || (isDark ? '#ffffff' : '#000000');

  const atAttachmentCap = attachments.length >= MAX_CHAT_IMAGE_ATTACHMENTS;
  const canAttach = !atAttachmentCap && uploadQueue.length === 0;

  const handleRemoveAttachment = useCallback(
    async (attachment: (typeof attachments)[number]) => {
      const attachmentId = attachment.id || attachment.fileId || attachment.url || '';
      if (!attachmentId || !onAttachmentRemove || deletingId) return;

      setDeletingId(attachmentId);
      try {
        const deleted = await deleteFile(attachment);
        if (deleted) {
          onAttachmentRemove(attachmentId);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      } catch (error) {
        logger.error(
          'Error deleting attachment',
          error instanceof Error ? { message: error.message } : error,
          'floating-input-core',
        );
      } finally {
        setDeletingId(null);
      }
    },
    [deletingId, onAttachmentRemove],
  );

  const renderAttachmentPreviews = () =>
    attachments.map((attachment) => {
      const attachmentId = attachment.id || attachment.fileId || attachment.url || '';
      const isThisDeleting = deletingId === attachmentId;

      return (
        <Attachments key={attachmentId} variant="inline" className="w-6 h-6 relative">
          <Attachment
            data={{
              id: attachmentId,
              type: 'file' as const,
              url: attachment.url || '',
              filename: attachment.name || attachment.filename || 'Attachment',
              mimeType: attachment.contentType,
            } as any}
            onRemove={undefined}
            className="relative w-full h-full"
          >
            <AttachmentPreview className="w-full h-full" />
            {isThisDeleting ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded z-10">
                <Loader2 className="w-3 h-3 text-white animate-spin" />
              </div>
            ) : (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void handleRemoveAttachment(attachment);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    void handleRemoveAttachment(attachment);
                  }
                }}
                className="absolute -top-1 -right-1 w-4 h-4 bg-neutral-600 hover:bg-neutral-700 dark:bg-neutral-400 dark:hover:bg-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center cursor-pointer z-10"
                aria-label="Remove attachment"
              >
                <X className="w-2 h-2 text-white dark:text-neutral-900" />
              </div>
            )}
          </Attachment>
        </Attachments>
      );
    });

  // Shared upload button (used as leading "+" in drawer mode, image icon otherwise)
  const renderUploadButton = (icon: React.ReactNode) => (
    <PromptInputButton
      type="button"
      variant="ghost"
      onClick={(e) => {
        e.preventDefault();
        if (canAttach && fileInputRef.current) {
          fileInputRef.current.click();
        }
      }}
      disabled={!canAttach}
      className={cx(
        "!gap-1 !px-1.5 !h-7 !py-1 !min-h-0 hover:!opacity-70",
        widgetStyle === 'black' && "!text-white hover:!bg-white/10",
        widgetStyle === 'glass' && "!text-white hover:!bg-white/10",
        widgetStyle !== 'black' && widgetStyle !== 'glass' && "!text-foreground hover:!bg-accent",
      )}
      title={
        atAttachmentCap
          ? "Remove an image to upload another"
          : uploadQueue.length > 0
            ? "Uploading..."
            : "Upload Image"
      }
    >
      {uploadQueue.length > 0 ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        icon
      )}
    </PromptInputButton>
  );

  return (
    <div 
      ref={containerRef}
      className={cx(
        drawerMode ? "w-full pt-1 overflow-visible" : "fixed bottom-4 left-1/2 -translate-x-1/2 px-2 overflow-visible",
        isDark ? 'dark' : '',
        className
      )}
      data-drawer-mode={drawerMode ? "true" : undefined}
      data-theme={theme}
      style={{
        ...customStyle,
        ...(!drawerMode && {
          width: isContinue ? 'auto' : (isClicked ? 'min(520px, calc(100vw - 2rem))' : 'auto'),
          maxWidth: isContinue ? '340px' : (isClicked ? '520px' : '340px'),
          minWidth: isContinue ? '260px' : (isClicked ? '300px' : '260px'),
          transform: isClicked && !isContinue ? 'translateY(-4px)' : 'translateY(0)',
          transition: 'width 0.32s cubic-bezier(0.32, 0.72, 0, 1), max-width 0.32s cubic-bezier(0.32, 0.72, 0, 1), min-width 0.32s cubic-bezier(0.32, 0.72, 0, 1), transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
          zIndex: customStyle?.zIndex ?? 10001, // Use custom z-index if provided, otherwise default to 10001
          pointerEvents: 'auto',
        }),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {!drawerMode && (
        <FloatingSuggestedBubbles
          questions={questions}
          visible={showSuggestions}
          theme={theme}
          accentColor={accentColor}
          onSelect={(question) => {
            if (onSuggestionSelect) {
              onSuggestionSelect(question);
              return;
            }
            setInput(question);
          }}
        />
      )}
      <PromptInput
        onSubmit={handleSubmit}
        onClick={(e) => {
          if (drawerMode) return;
          const target = e.target as HTMLElement;
          if (target.closest('textarea') || target.closest('button')) {
            return;
          }
          if (hasActiveConversation) {
            onContinue?.();
            return;
          }
          textareaRef.current?.focus();
          setIsFocused(true);
          setIsClicked(true);
        }}
        data-drawer-mode={drawerMode ? "true" : undefined}
        className={cx(
          getInputClasses(),
          "rounded-full",
          !drawerMode && "shadow-lg",
          // Keep the focus ring from being clipped in chat/drawer mode
          // Pill chrome stays clipped; chips render as a sibling above this form.
          drawerMode ? "!overflow-visible" : "!overflow-hidden",
          "flex items-center",
          "transition-all duration-200 ease-in-out",
          !drawerMode && "hover:shadow-xl",
          compact ? "!p-0.5 !gap-1 !min-h-[36px] !pl-2 !pr-1" : "!p-1 !gap-1.5 !min-h-[48px] !pl-3 !pr-1.5"
        )}
        style={
          drawerMode
            ? {
                // Muted pill: clean at rest, a subtle thin border on hover, and a
                // thin accent focus ring on select (fallback black/white by theme).
                borderColor: isActive
                  ? 'transparent'
                  : isHovered
                    ? (isDark ? '#52525b' : '#d4d4d4')
                    : 'transparent',
                borderWidth: '1px',
                boxShadow: isActive ? `0 0 0 2px ${ringColor}` : 'none',
                transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
                outline: 'none',
                outlineOffset: 0,
              }
            : {
                // Focus ring replaces border: when focused, use accent color border; when not focused, use gray border
                borderColor: isActive
                  ? (accentColor || (isDark ? '#525252' : '#d4d4d4'))
                  : (isDark ? '#525252' : '#d4d4d4'),
                borderWidth: '1px',
                boxShadow: isClicked
                  ? (isDark
                    ? '0 16px 44px rgba(0, 0, 0, 0.5)'
                    : '0 16px 44px rgba(0, 0, 0, 0.14)')
                  : (isDark
                    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                    : '0 8px 32px rgba(0, 0, 0, 0.08)'),
                transform: isHovered && !isClicked ? 'scale(1.015)' : 'scale(1)',
                transition: 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1), box-shadow 0.28s ease, border-color 0.2s ease',
                outline: 'none',
                outlineOffset: 0,
              }
        }
      >
        {/* Leading slot (chat/drawer mode): "+" upload when vision enabled, else keyboard shortcut hint */}
        {drawerMode && chatFileAttachementEnabled && (
          <div className="flex items-center !flex-shrink-0 mr-0.5 gap-1">
            {renderAttachmentPreviews()}
            {renderUploadButton(<IconPlus size={18} />)}
          </div>
        )}
        {((drawerMode && !chatFileAttachementEnabled) || (!drawerMode && !isContinue)) && (
          <div className="flex items-center !flex-shrink-0 opacity-50 select-none mr-1">
            <IconCommand className={cx(
              "size-3.5",
              isDark ? "text-neutral-400" : "text-neutral-500"
            )} />
          </div>
        )}

        {/* Hidden file input */}
        {chatFileAttachementEnabled && (
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/jpeg,image/png,image/gif,image/webp,image/bmp,image/tiff"
            multiple
            className="hidden"
          />
        )}

        
        <PromptInputTextarea
          ref={setTextareaRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            adjustHeight();
          }}
          onFocus={(e) => {
            e.stopPropagation();
            if (!drawerMode && hasActiveConversation) {
              e.target.blur();
              onContinue?.();
              return;
            }
            setIsFocused(true);
            if (!drawerMode) {
              setIsClicked(true);
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!drawerMode && hasActiveConversation) {
              onContinue?.();
              return;
            }
            setIsFocused(true);
            if (!drawerMode) {
              setIsClicked(true);
            }
          }}
          readOnly={isContinue}
          onBlur={handleBlur}
          placeholder={effectivePlaceholder}
          data-placeholder-gray="true"
          style={{
            // Inline styles beat the shadcn base classes (field-sizing-content +
            // max-h-[6lh]) so the empty pill can't balloon on mobile. Height itself
            // is set imperatively in adjustHeight.
            fieldSizing: 'fixed',
            minHeight: compact ? 20 : 24,
            maxHeight: 200,
          } as React.CSSProperties}
          className={cx(
            getTextareaClasses(),
            "!px-0 !m-0",
            "flex-1 self-center",
            compact
              ? "!py-0 !leading-5 !text-xs"
              : "!py-0 !leading-6 !text-sm",
            !drawerMode && "whitespace-nowrap overflow-hidden text-ellipsis",
            inputClassName
          )}
          rows={1}
          minHeight={compact ? 20 : 24}
          maxHeight={compact ? 120 : 200}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e as any);
            }
          }}
        />
        
        <PromptInputToolbar className="!p-0 !gap-2 !flex-shrink-0 !h-full !items-center">
          <PromptInputTools className="!p-0 !gap-1 !h-full !items-center">
            {/* Attachment button (floating pill only - drawer mode uses the leading "+" slot) */}
            {!drawerMode && !isContinue && chatFileAttachementEnabled && (
              <>
                {renderAttachmentPreviews()}
                {renderUploadButton(<IconPhotoPlus size={16} />)}
              </>
            )}

            {/* Voice mode button (floating pill only - drawer mode uses the Call/Send morph) */}
            {!drawerMode && !isContinue && chatVoiceEnabled && (
              <button
                onClick={onVoiceClick}
                type="button"
                style={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                  padding: 0,
                  borderRadius: '50%',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: phoneButtonBg,
                  color: '#ffffff',
                  opacity: 1,
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  flexShrink: 0,
                  lineHeight: 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.95)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                aria-label="Voice mode"
              >
                <IconPhoneSpark className="size-4" />
              </button>
            )}
          </PromptInputTools>

          {/* Primary control:
              - drawer mode + voice enabled + empty input => "Call" pill (morphs to Send once typing)
              - otherwise => circular Send button */}
          {isContinue ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onContinue?.();
              }}
              className={cx(
                "flex items-center justify-center rounded-full text-white transition-transform duration-150 ease-out",
                compact ? "size-7" : "size-8",
                "cursor-pointer hover:scale-105 active:scale-95"
              )}
              style={{
                backgroundColor: accentColor || '#000000',
                flexShrink: 0,
                lineHeight: 1,
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              }}
              aria-label="Continue conversation"
            >
              <IconChevronRight className={compact ? "size-3.5" : "size-4"} strokeWidth={2.25} />
            </button>
          ) : drawerMode && chatVoiceEnabled && !hasContent && !isLoading ? (
            <button
              type="button"
              onClick={onVoiceClick}
              className={cx(
                "flex items-center gap-1.5 rounded-full font-medium text-white",
                "transition-transform duration-150 ease-out hover:scale-105 active:scale-95",
                compact ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-sm"
              )}
              style={{ backgroundColor: phoneButtonBg, flexShrink: 0, lineHeight: 1 }}
              aria-label="Start voice call"
            >
              <IconWaveSine className={compact ? "size-3.5" : "size-4"} />
              <span>Call</span>
            </button>
          ) : (
            <button
              type="submit"
              disabled={!hasContent || isLoading || disabled || uploadQueue.length > 0}
              className={cx(
                "flex items-center justify-center rounded-full text-white transition-transform duration-150 ease-out",
                compact ? "size-7" : "size-8",
                (hasContent && !isLoading && !disabled)
                  ? "cursor-pointer hover:scale-105 active:scale-95"
                  : "cursor-not-allowed"
              )}
              style={{
                backgroundColor: sendButtonBg,
                flexShrink: 0,
                lineHeight: 1,
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              }}
              aria-label="Send message"
            >
              {isLoading ? (
                <Loader2 className={compact ? "size-3.5 animate-spin" : "size-4 animate-spin"} strokeWidth={2.5} />
              ) : (
                <IconArrowUp className={compact ? "size-3.5" : "size-4"} strokeWidth={2.5} />
              )}
            </button>
          )}
        </PromptInputToolbar>
      </PromptInput>
    </div>
  );
}
